/**
 * The rules that decide what an application step sends, kept apart from the screen so they run (and are tested) without a browser.
 */

export interface EmploymentEntry {
  job_type: string;
  annual_income: number | '';
  title: string;
  company_name: string;
  currently_work_here: boolean;
  start_date: string;
  end_date: string;
  supervisor_name: string;
  supervisor_email: string;
  supervisor_phone: string;
}

export const SUPERVISOR_FIELDS = ['supervisor_name', 'supervisor_email', 'supervisor_phone'] as const;

/** A reference with nothing in it is no reference: opened and closed again, it changes nothing that is sent. */
export function hasSupervisor(entry: Pick<EmploymentEntry, (typeof SUPERVISOR_FIELDS)[number]>): boolean {
  return SUPERVISOR_FIELDS.some((field) => entry[field].trim().length > 0);
}

/** One job as it is sent: the reference's keys are there only when the applicant filled something in. */
export function employmentRow(entry: EmploymentEntry): Record<string, unknown> {
  const { supervisor_name, supervisor_email, supervisor_phone, ...job } = entry;
  const reference = { supervisor_name, supervisor_email, supervisor_phone };
  return hasSupervisor(entry) ? { ...job, ...reference } : job;
}

/**
 * The employment step's payload for these jobs. The first job is also mirrored at the top level (the shape the API reads),
 * with an empty reference as `null`, never as an empty string.
 */
export function employmentPayload(entries: EmploymentEntry[], blank: EmploymentEntry): Record<string, unknown> {
  const primary = entries[0] ?? blank;
  const income = typeof primary.annual_income === 'number' && Number.isFinite(primary.annual_income) ? primary.annual_income : null;
  const reference = hasSupervisor(primary);
  return {
    employments: entries.map(employmentRow),
    job_type: primary.job_type || null,
    annual_income: income,
    title: primary.title || null,
    company_name: primary.company_name || null,
    currently_work_here: primary.currently_work_here,
    start_date: primary.start_date || null,
    end_date: primary.end_date || null,
    supervisor_name: reference ? primary.supervisor_name || null : null,
    supervisor_email: reference ? primary.supervisor_email || null : null,
    supervisor_phone: reference ? primary.supervisor_phone || null : null,
  };
}

/** The same address however it was typed or stored: the API compares trimmed, lower-case. */
export function normalizeEmail(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

/**
 * The About you payload always carries the verified address. The applicant cannot edit it on the page, and a draft saved before that rule
 * (or by another client) may hold a different one: this overrides it, never falls back to it, so a submission cannot fail on the address.
 */
export function withVerifiedEmail(payload: Record<string, unknown>, verifiedEmail: string): Record<string, unknown> {
  return verifiedEmail ? { ...payload, email: verifiedEmail } : payload;
}

/** True when a saved About you payload holds an address other than the verified one and needs saving again. */
export function needsEmailRepair(saved: Record<string, unknown> | undefined, verifiedEmail: string): boolean {
  if (!saved || !verifiedEmail) return false;
  const stored = normalizeEmail(saved.email);
  return stored !== '' && stored !== normalizeEmail(verifiedEmail);
}
